import type { Bound, SerializedXYWH, XYWH } from '@blocksuite/global/gfx';
import { deserializeXYWH } from '@blocksuite/global/gfx';
import { assertType, noop } from '@blocksuite/global/utils';
import type {
  BaseElementProps,
  GfxModel,
  GfxPrimitiveElementModel,
  PointTestOptions,
  SerializedElement,
} from '@blocksuite/std/gfx';
import {
  convert,
  field,
  GfxGroupLikeElementModel,
  observe,
  watch,
} from '@blocksuite/std/gfx';
import { generateKeyBetween } from 'fractional-indexing';
import last from 'lodash-es/last';
import pick from 'lodash-es/pick';
import * as Y from 'yjs';
import { z } from 'zod';

import { ConnectorMode } from '../../consts/connector.js';
import { LayoutType, MindmapStyle } from '../../consts/mindmap.js';
import { LocalConnectorElementModel } from '../connector/local-connector.js';
import type { MindmapStyleGetter } from './style.js';
import { mindmapStyleGetters } from './style.js';
import { findInfiniteLoop } from './utils.js';

/**
 * Which side of the root a first-level branch sits on, in the balanced layouts.
 * `right` is the right of the root on the horizontal axis, and *below* it on the
 * vertical one; `left` is the mirror of that (left / above).
 */
export type NodeSide = 'left' | 'right';

export type NodeDetail = {
  /**
   * The index of the node, it decides the layout order of the node
   */
  index: string;
  parent?: string;
  collapsed?: boolean;
  /**
   * Pins a first-level branch to one side of the root, so that the two sides are
   * INDEPENDENT: adding a branch to one side can never shunt an existing one
   * across to the other.
   *
   * Absent on nodes that predate this field, and on deeper nodes (which have no
   * side of their own — they follow their first-level ancestor). Nodes without
   * it fall back to the original halve-by-index rule, so documents made before
   * this open looking exactly as they did.
   */
  side?: NodeSide;
  /**
   * ROOT ONLY: the sides that are collapsed. A balanced root grows branches both
   * ways, so it gets a collapse button per side rather than one that folds the
   * whole map away — `collapsed` above stays for every other node, and for the
   * root of a one-sided layout.
   */
  collapsedSides?: NodeSide[];
};

export type MindmapNode = {
  id: string;
  detail: NodeDetail;

  element: GfxPrimitiveElementModel;
  children: MindmapNode[];

  parent: MindmapNode | null;

  /**
   * This area is used to determine where to place the dragged node.
   *
   * When dragging another node into this area, it will become a sibling of the this node.
   * But if it is dragged into the small area located right after the this node, it will become a child of the this node.
   */
  responseArea?: Bound;

  /**
   * This property override the preferredDir or default layout direction.
   * It is used during dragging that would temporary change the layout direction
   */
  overriddenDir?: LayoutType;
};

export type MindmapRoot = MindmapNode & {
  left: MindmapNode[];
  right: MindmapNode[];
};

const baseNodeSchema = z.object({
  text: z.string(),
  xywh: z.optional(z.string()),
});

type Node = z.infer<typeof baseNodeSchema> & {
  children?: Node[];
};

const nodeSchema: z.ZodType<Node> = baseNodeSchema.extend({
  children: z.lazy(() => nodeSchema.array()).optional(),
});

export type NodeType = z.infer<typeof nodeSchema>;

function isNodeType(node: Record<string, unknown>): node is NodeType {
  return typeof node.text === 'string' && Array.isArray(node.children);
}

export type SerializedMindmapElement = SerializedElement & {
  children: Record<string, NodeDetail>;
};

type MindmapElementProps = BaseElementProps & {
  children: Y.Map<NodeDetail>;
};

function observeChildren(
  _: unknown,
  instance: MindmapElementModel,
  transaction: Y.Transaction | null
) {
  if (instance.children.doc) {
    const local = transaction?.local ?? true;

    instance.setChildIds(Array.from(instance.children.keys()), local);

    instance.buildTree();
    instance.connectors.clear();

    // Undo and redo rewind the children map without going back through addNode
    // or the drag handlers, so nothing re-runs the layout and the branches are
    // left standing wherever the OLD tree put them. Ask for one.
    //
    // Only for local changes: a remote peer's edit arrives with its positions
    // already in it, and re-deriving them here would have every client racing to
    // write the same coordinates back.
    if (local) {
      instance.requestLayout();
    }
  }
}

function watchLayoutType(
  _: unknown,
  instance: MindmapElementModel,
  local: boolean
) {
  if (!local) {
    return;
  }

  // Only a balanced layout draws two sides, and so only it has a per-side
  // collapse button. Moving to a one-sided layout with a side folded away would
  // leave those branches hidden with no way to get them back.
  if (
    instance.layoutType !== LayoutType.BALANCE &&
    instance.layoutType !== LayoutType.BALANCE_VERTICAL
  ) {
    instance.expandAllSides();
  }

  instance.surface.store.transact(() => {
    instance['_tree']?.children.forEach(child => {
      // Read the detail back out of the map, NOT off `_tree` — the tree is a
      // snapshot from the last build, so its `detail` objects predate anything
      // written earlier in this same tick. Upstream wrote a fresh
      // `{index, parent}` from that stale snapshot, which silently dropped
      // `collapsed` and would wipe a `side` pinned moments ago (this watcher
      // runs when the surrounding transaction commits, i.e. *after* the write).
      const detail = instance.children.get(child.id);

      if (!detail) {
        return;
      }

      // Rewriting it is what nudges the first-level branches into re-laying out
      // when the layout flips.
      instance.children.set(child.id, { ...detail });
    });
  });

  instance.buildTree();
}

function watchStyle(_: unknown, instance: MindmapElementModel, local: boolean) {
  if (!local) return;
  instance.layout();
}

export class MindmapElementModel extends GfxGroupLikeElementModel<MindmapElementProps> {
  private _layout: MindmapElementModel['layout'] | null = null;

  private _nodeMap = new Map<string, MindmapNode>();

  private _queueBuildTree = false;

  private _queuedLayout = false;

  private readonly _stashedNode = new Set<string>();

  private _tree!: MindmapRoot;

  connectors = new Map<string, LocalConnectorElementModel>();

  get nodeMap() {
    return this._nodeMap;
  }

  override get rotate() {
    return 0;
  }

  override set rotate(_: number) {}

  get styleGetter(): MindmapStyleGetter {
    return mindmapStyleGetters[this.style];
  }

  get tree() {
    return this._tree;
  }

  get type() {
    return 'mindmap';
  }

  static propsToY(props: Record<string, unknown>) {
    if (
      props.children &&
      !isNodeType(props.children as Record<string, unknown>) &&
      !(props.children instanceof Y.Map)
    ) {
      const children: Y.Map<NodeDetail> = new Y.Map();

      Object.entries(props.children).forEach(([key, value]) => {
        // An allowlist, so every key of NodeDetail has to be named here. It used
        // to list only index+parent, quietly dropping `collapsed` — and it would
        // drop the pinned `side` — whenever a mind map came in as a plain object
        // rather than a Y.Map (converting a selection to a linked doc does this).
        const detail = pick<Record<string, unknown>, keyof NodeDetail>(value, [
          'index',
          'parent',
          'collapsed',
          'side',
          'collapsedSides',
        ]);
        children.set(key as string, detail as NodeDetail);
      });

      props.children = children;
    }

    return props as MindmapElementProps;
  }

  private _cfgBalanceLayoutDir() {
    if (
      this.layoutType !== LayoutType.BALANCE &&
      this.layoutType !== LayoutType.BALANCE_VERTICAL
    ) {
      return;
    }

    const tree = this._tree;

    // A branch that has been pinned to a side stays there. The rest are split
    // down the middle, which is what this used to do to *all* of them — that
    // rule is why adding one branch could shove another across to the far side.
    // Keeping it for the unpinned ones means documents written before `side`
    // existed still open exactly as they did.
    const unpinned = tree.children.filter(
      child => child.detail.side === undefined
    );
    const splitPoint = Math.ceil(unpinned.length / 2);
    const impliedRight = new Set(unpinned.slice(0, splitPoint));

    tree.children.forEach(child => {
      const side =
        child.detail.side ?? (impliedRight.has(child) ? 'right' : 'left');

      (side === 'right' ? tree.right : tree.left).push(child);
    });

    tree.left.reverse();
  }

  private _isConnectorOutdated(
    options:
      | {
          connector: LocalConnectorElementModel;
          from: MindmapNode;
          to: MindmapNode;
          layout: LayoutType;
        }
      | {
          connector: LocalConnectorElementModel;
          from: MindmapNode;
          layout: LayoutType;
          collapsed: boolean;
        },
    updateKey: boolean = true
  ) {
    const hasFromTo = 'to' in options;
    const { connector, from, layout } = options;

    if (!from?.element) {
      return { outdated: true, cacheKey: '' };
    }
    if (hasFromTo && !options.to?.element) {
      return { outdated: true, cacheKey: '' };
    }

    const cacheKey = !hasFromTo
      ? `${from.element.xywh}-collapsed-${layout}-${this.style}`
      : `${from.element.xywh}-${options.to.element.xywh}-${layout}-${this.style}`;

    if (connector.cache.get('MINDMAP_CONNECTOR') === cacheKey) {
      return false;
    } else if (updateKey) {
      connector.cache.set('MINDMAP_CONNECTOR', cacheKey);
    }

    return true;
  }

  protected override _getXYWH(): Bound {
    return super._getXYWH();
  }

  /**
   * @deprecated
   * you should not call this method directly
   */
  addChild(_element: GfxModel) {
    noop();
  }

  /**
   * The side a node about to be added should be pinned to: the side of the
   * sibling it is being created next to, when that sibling is a branch of the
   * root and the map is balanced. Null in every other case — the node either has
   * no side to speak of, or the layout has only one.
   */
  private _sideToInherit(
    parent: string | null,
    sibling?: string | number
  ): NodeSide | null {
    const balanced =
      this.layoutType === LayoutType.BALANCE ||
      this.layoutType === LayoutType.BALANCE_VERTICAL;

    if (!balanced || !parent || parent !== this._tree?.id) {
      return null;
    }

    // Only when the caller named a sibling. A bare append is a different
    // question — see `addNode`, which defaults it to the last child — and
    // answering it here would pin every branch of a mind map as it was built.
    if (sibling === undefined) {
      return null;
    }

    const siblingId =
      typeof sibling === 'number'
        ? this._tree.children[sibling]?.id
        : sibling;

    return siblingId ? this.getSide(siblingId) : null;
  }

  addNode(
    /**
     * The parent node id of the new node. If it's null, the node will be the root node
     */
    parent: string | MindmapNode | null,
    sibling?: string | number,
    position: 'before' | 'after' = 'after',
    props: Record<string, unknown> = {}
  ) {
    if (parent && typeof parent !== 'string') {
      parent = parent.id;
    }

    assertType<string | null>(parent);

    if (parent && !this._nodeMap.has(parent)) {
      throw new Error(`Parent node ${parent} not found`);
    }

    props['text'] = new Y.Text((props['text'] as string) ?? 'New node');

    // A new FIRST-LEVEL branch takes the side of the branch it was created next
    // to. Enter (and the sibling button) used to hand it no side at all, which
    // left it to the halve-by-index fallback — free to drop it on the FAR side of
    // the root, and to shove the others around on its way.
    //
    // Only in the balanced layouts: a one-sided map has nothing to get wrong, and
    // pinning there would stop the Radial option from ever splitting it again.
    const inheritedSide = this._sideToInherit(parent, sibling);

    const type = (props.type as string) ?? 'shape';
    let id: string;
    this.surface.store.transact(() => {
      if (inheritedSide) {
        // Freeze the others where they are — pinning one branch on its own leaves
        // the rest to the fallback, which reshuffles them as the count changes.
        // Inside this transaction, so the whole add is a single step to undo.
        this.pinCurrentSides();
      }

      const parentNode = parent ? this._nodeMap.get(parent)! : null;

      if (parentNode) {
        let index = last(parentNode.children)
          ? generateKeyBetween(last(parentNode.children)!.detail.index, null)
          : 'a0';

        sibling = sibling ?? last(parentNode.children)?.id;
        const siblingNode =
          typeof sibling === 'number'
            ? parentNode.children[sibling]
            : sibling
              ? this._nodeMap.get(sibling)
              : undefined;
        const path = siblingNode
          ? this.getPath(siblingNode)
          : this.getPath(parentNode).concat([0]);
        const style = this.styleGetter.getNodeStyle(
          siblingNode ?? parentNode,
          path
        );

        id = this.surface.addElement({
          type,
          xywh: '[0,0,100,30]',
          maxWidth: false,
          ...props,
          ...style.node,
        });

        if (siblingNode) {
          const siblingIndex = parentNode.children.findIndex(
            val => val.id === sibling
          );

          index =
            position === 'after'
              ? generateKeyBetween(
                  siblingNode.detail.index,
                  parentNode.children[siblingIndex + 1]?.detail.index ?? null
                )
              : generateKeyBetween(
                  parentNode.children[siblingIndex - 1]?.detail.index ?? null,
                  siblingNode.detail.index
                );
        }

        const nodeDetail: NodeDetail = {
          index,
          parent: parent!,
        };

        if (inheritedSide) {
          nodeDetail.side = inheritedSide;
        }

        this.children.set(id, nodeDetail);
      } else {
        const rootStyle = this.styleGetter.root;

        id = this.surface.addElement({
          type,
          xywh: '[0,0,113,41]',
          maxWidth: false,
          ...props,
          ...rootStyle,
        });

        this.children.clear();
        this.children.set(id, {
          index: 'a0',
        });
      }
    });
    this.layout();

    return id!;
  }

  buildTree() {
    const mindmapNodeMap = new Map<string, MindmapNode>();
    const nodesMap = this.children;

    // The element may be removed
    if (!nodesMap || nodesMap.size === 0) {
      this._nodeMap = mindmapNodeMap;
      // @ts-expect-error FIXME: ts error
      this._tree = null;
      return;
    }

    let rootNode: MindmapRoot | undefined;

    nodesMap.forEach((val, id) => {
      if (!mindmapNodeMap.has(id)) {
        mindmapNodeMap.set(id, {
          id,
          index: val.index,
          detail: val,
          element: this.surface.getElementById(id)!,
          children: [],
          parent: null,
        } as MindmapNode);
      }

      const node = mindmapNodeMap.get(id)!;

      // some node may be already created during
      // iterating its children
      if (!node.detail) {
        node.detail = val;
      }

      if (!val.parent) {
        rootNode = node as MindmapRoot;
        rootNode.left = [];
        rootNode.right = [];
      } else {
        if (!mindmapNodeMap.has(val.parent)) {
          mindmapNodeMap.set(val.parent, {
            id: val.parent,
            detail: nodesMap.get(val.parent)!,
            parent: null,
            children: [],
            element: this.surface.getElementById(val.parent)!,
          } as MindmapNode);
        }

        const parent = mindmapNodeMap.get(val.parent)!;
        parent.children.push(node);
        node.parent = parent;
      }
    });

    mindmapNodeMap.forEach(node => {
      node.children.sort((a, b) =>
        a.detail.index === b.detail.index
          ? 0
          : a.detail.index > b.detail.index
            ? 1
            : -1
      );
    });

    if (!rootNode) {
      return;
    }

    const loops = findInfiniteLoop(rootNode, mindmapNodeMap);

    if (loops.length) {
      this.surface.store.withoutTransact(() => {
        loops.forEach(loop => {
          if (loop.detached) {
            loop.chain.forEach(node => {
              this.children.delete(node.id);
            });
          } else {
            const child = last(loop.chain);

            if (child) {
              this.children.set(child.id, {
                index: child.detail.index,
              });
            }
          }
        });
      });
      return;
    }

    this._nodeMap = mindmapNodeMap;
    this._tree = rootNode;

    if (
      this.layoutType === LayoutType.BALANCE ||
      this.layoutType === LayoutType.BALANCE_VERTICAL
    ) {
      // Both balanced layouts split the first-level branches into the same two
      // buckets; only the axis they're laid out on differs.
      this._cfgBalanceLayoutDir();
    } else if (
      this.layoutType === LayoutType.RIGHT ||
      this.layoutType === LayoutType.LEFT
    ) {
      this._tree[this.layoutType === LayoutType.RIGHT ? 'right' : 'left'] =
        this._tree.children;
    }
    // TOP / BOTTOM grow along a single axis, so they need no left/right split.
  }

  /**
   *
   * @param subtree The subtree of root, this only take effects when the layout type is BALANCED.
   * @returns
   */
  getChildNodes(id: string, subtree?: 'left' | 'right') {
    const node = this._nodeMap.get(id);

    if (!node) {
      return [];
    }

    if (subtree && id === this._tree.id) {
      return this._tree[subtree];
    }

    return node.children;
  }

  /**
   * The stub that runs from a node's edge to the badge standing in for the
   * branches folded away behind it.
   *
   * @param layout Which edge the folded branches left from. A per-side collapse
   *   passes the direction of that side; a whole-node collapse passes the node's
   *   own direction.
   * @param key Distinguishes the two stubs a balanced root can have at once.
   */
  private _collapsedStubConnector(
    node: MindmapNode,
    layout: LayoutType,
    key = 'collapsed'
  ) {
    const id = `#${node.id}-${key}`;
    const connector =
      this.connectors.get(id) ?? new LocalConnectorElementModel(this.surface);
    const connectorExist = this.connectors.has(id);
    const connectorStyle = this.styleGetter.getNodeStyle(
      node,
      this.getPath(node).concat([0])
    ).connector;
    const outdated = this._isConnectorOutdated({
      connector,
      from: node,
      collapsed: true,
      layout,
    });

    if (!connectorExist) {
      connector.id = id;
      this.connectors.set(id, connector);
    }

    if (outdated) {
      const nodeBound = node.element.elementBound;
      connector.id = id;
      // Anchor on the edge the branch leaves from: bottom for TOP (grows
      // downward), top for BOTTOM (grows upward), left/right otherwise.
      connector.source = {
        id: node.id,
        position:
          layout === LayoutType.TOP
            ? [0.5, 1]
            : layout === LayoutType.BOTTOM
              ? [0.5, 0]
              : layout === LayoutType.LEFT
                ? [0, 0.5]
                : [1, 0.5],
      };
      connector.target = {
        position:
          layout === LayoutType.TOP
            ? [nodeBound.x + nodeBound.w / 2, nodeBound.y + nodeBound.h + 6]
            : layout === LayoutType.BOTTOM
              ? [nodeBound.x + nodeBound.w / 2, nodeBound.y - 6]
              : layout === LayoutType.LEFT
                ? [nodeBound.x - 6, nodeBound.y + nodeBound.h / 2]
                : [nodeBound.x + nodeBound.w + 6, nodeBound.y + nodeBound.h / 2],
      };

      Object.entries(connectorStyle).forEach(([key, value]) => {
        // @ts-expect-error FIXME: ts error
        connector[key as unknown] = value;
      });

      connector.mode = ConnectorMode.Straight;
    }

    return { outdated, connector };
  }

  /** The edge a side's branches leave the root from. */
  private _sideLayoutDir(side: NodeSide): LayoutType {
    if (this.layoutType === LayoutType.BALANCE_VERTICAL) {
      // `right` is the downward half — and TOP is the layout whose branches grow
      // downward. `left` is the upward half, which is BOTTOM.
      return side === 'right' ? LayoutType.TOP : LayoutType.BOTTOM;
    }

    return side === 'right' ? LayoutType.RIGHT : LayoutType.LEFT;
  }

  /**
   * Get all the connectors start from the given node
   * @param node
   * @returns
   */
  getConnectors(node: MindmapNode) {
    if (!this._nodeMap.has(node.id)) {
      return null;
    }

    if (node.detail.collapsed) {
      return [
        this._collapsedStubConnector(node, this.getLayoutDir(node)!),
      ];
    } else {
      const from = node;
      // A branch folded away by a per-side collapse is hidden, but the root it
      // hangs off is NOT flagged `collapsed` — the other side is still open. So
      // skip connectors to hidden branches here, or their curves keep being drawn
      // out across the empty space to nodes that aren't there.
      const visible = from.children.filter(to => !to.element.hidden);

      // Each folded side instead gets a stub out to the badge that stands in for
      // it, so the count doesn't float loose beside the root.
      const stubs =
        from.id === this._tree.id
          ? (['left', 'right'] as NodeSide[])
              .filter(side => this.isSideCollapsed(side))
              .map(side =>
                this._collapsedStubConnector(
                  from,
                  this._sideLayoutDir(side),
                  `collapsed-${side}`
                )
              )
          : [];

      return visible
        .map(to => {
        const layout = this.getLayoutDir(to)!;
        const id = `#${from.id}-${to.id}`;
        const connectorExist = this.connectors.has(id);
        const connectorStyle = this.styleGetter.getNodeStyle(
          to,
          this.getPath(to)
        ).connector;
        const connector =
          this.connectors.get(id) ??
          new LocalConnectorElementModel(this.surface);
        const outdated = this._isConnectorOutdated({
          connector,
          from,
          to,
          layout,
        });

        if (!connectorExist) {
          connector.id = id;
          this.connectors.set(id, connector);
        }

        if (outdated) {
          // Leave the parent from the edge facing the child, and enter the child
          // on its opposite edge.
          connector.source = {
            id: from.id,
            position:
              layout === LayoutType.TOP
                ? [0.5, 1]
                : layout === LayoutType.BOTTOM
                  ? [0.5, 0]
                  : layout === LayoutType.RIGHT
                    ? [1, 0.5]
                    : [0, 0.5],
          };
          connector.target = {
            id: to.id,
            position:
              layout === LayoutType.TOP
                ? [0.5, 0]
                : layout === LayoutType.BOTTOM
                  ? [0.5, 1]
                  : layout === LayoutType.RIGHT
                    ? [0, 0.5]
                    : [1, 0.5],
          };

          Object.entries(connectorStyle).forEach(([key, value]) => {
            // @ts-expect-error FIXME: ts error
            connector[key as unknown] = value;
          });
        }

        return { outdated, connector };
        })
        .concat(stubs);
    }
  }

  getLayoutDir(node: string | MindmapNode): LayoutType {
    node = typeof node === 'string' ? this._nodeMap.get(node)! : node;

    assertType<MindmapNode>(node);

    let current: MindmapNode | null = node;
    const root = this._tree;

    while (current) {
      if (current.overriddenDir !== undefined) {
        return current.overriddenDir;
      }

      const parent: MindmapNode | null = current.detail.parent
        ? (this._nodeMap.get(current.detail.parent) ?? null)
        : null;

      if (parent === root) {
        if (parent.overriddenDir !== undefined) {
          return parent.overriddenDir;
        }

        // The balanced layouts fill the same two buckets; which concrete
        // direction a bucket means depends on the axis.
        const [firstBucketDir, secondBucketDir] =
          this.layoutType === LayoutType.BALANCE_VERTICAL
            ? ([LayoutType.BOTTOM, LayoutType.TOP] as const) // left -> up, right -> down
            : ([LayoutType.LEFT, LayoutType.RIGHT] as const);

        return root.left.includes(current)
          ? firstBucketDir
          : root.right.includes(current)
            ? secondBucketDir
            : this.layoutType;
      }

      current = parent;
    }

    return this.layoutType;
  }

  getNode(id: string) {
    return this._nodeMap.get(id) ?? null;
  }

  /**
   * The side of the root a first-level branch currently sits on, whether that
   * was pinned explicitly or merely implied by the halve-by-index fallback.
   * Returns null for the root itself and for deeper nodes, which have no side of
   * their own.
   */
  getSide(id: string): NodeSide | null {
    const node = this._nodeMap.get(id);

    if (!node || node === this._tree || node.detail.parent !== this._tree.id) {
      return null;
    }

    if (node.detail.side) {
      return node.detail.side;
    }

    // Not pinned: read it off the layout as it stands. In the one-sided layouts
    // every branch is on the same side; in the balanced ones the buckets have
    // already been filled by `_cfgBalanceLayoutDir`.
    switch (this.getLayoutDir(node)) {
      case LayoutType.LEFT:
      case LayoutType.BOTTOM: // branches grow upward, i.e. the `left` bucket
        return 'left';
      default:
        return 'right';
    }
  }

  /**
   * Freeze every first-level branch on the side it is on right now.
   *
   * Pinning one branch without doing this leaves the others to the
   * halve-by-index fallback, which reshuffles them as soon as the child count
   * changes — so pinning one would fling another across the root. Call this
   * before any operation that pins a side.
   */
  pinCurrentSides() {
    // Read every side BEFORE writing any, so the buckets can't shift underfoot.
    const sides = this._tree.children.map(
      child => [child.id, this.getSide(child.id)] as const
    );

    this.surface.store.transact(() => {
      sides.forEach(([id, side]) => {
        if (side) {
          this.setSide(id, side);
        }
      });
    });
  }

  /**
   * Pin a first-level branch to one side of the root. Only the balanced layouts
   * draw the two sides, but pinning is harmless elsewhere — it just decides
   * where the branch lands if the map is later switched to a balanced layout.
   */
  setSide(id: string, side: NodeSide) {
    const detail = this.children.get(id);

    if (!detail || detail.side === side) {
      return;
    }

    this.surface.store.transact(() => {
      this.children.set(id, { ...detail, side });
    });
  }

  getNodeByPath(path: number[]): MindmapNode | null {
    let node: MindmapNode | null = this._tree;
    for (let i = 1; i < path.length; i++) {
      node = node?.children[path[i]];
      if (!node) return null;
    }
    return node;
  }

  getParentNode(id: string) {
    const node = this.children.get(id);

    return node?.parent ? (this._nodeMap.get(node.parent) ?? null) : null;
  }

  /**
   * Path is an array of indexes that represent the path from the root node to the target node.
   * The first element of the array is always 0, which represents the root node.
   * @param element
   * @returns
   *
   * @example
   * ```ts
   * const path = mindmap.getPath('nodeId');
   * // [0, 1, 2]
   * ```
   */
  getPath(element: string | MindmapNode) {
    let node = this._nodeMap.get(
      typeof element === 'string' ? element : element.id
    );

    if (!node) {
      throw new Error('Node not found');
    }

    const path: number[] = [];

    while (node && node !== this._tree) {
      const parent = this._nodeMap.get(node!.detail.parent!);

      path.unshift(parent!.children.indexOf(node!));
      node = parent;
    }

    path.unshift(0);

    return path;
  }

  getSiblingNode(
    id: string,
    direction: 'prev' | 'next' = 'next',
    /**
     * The subtree of which that the sibling node belongs to,
     * this is used when the layout type is BALANCED.
     */
    subtree?: 'left' | 'right'
  ) {
    const node = this._nodeMap.get(id);

    if (!node) {
      return null;
    }

    const parent = this._nodeMap.get(node.detail.parent!);

    if (!parent) {
      return null;
    }

    const childrenTree =
      subtree && parent.id === this._tree.id
        ? this._tree[subtree]
        : parent.children;
    const idx = childrenTree.indexOf(node);
    if (idx === -1) {
      return null;
    }
    const siblingIndex = direction === 'next' ? idx + 1 : idx - 1;
    const sibling = childrenTree[siblingIndex] ?? null;

    return sibling;
  }

  override includesPoint(x: number, y: number, options: PointTestOptions) {
    const bound = this.elementBound;

    bound.x -= options.responsePadding?.[0] ?? 0;
    bound.w += (options.responsePadding?.[0] ?? 0) * 2;
    bound.y -= options.responsePadding?.[1] ?? 0;
    bound.h += (options.responsePadding?.[1] ?? 0) * 2;

    return bound.containsPoint([x, y]);
  }

  layout(
    _tree: MindmapNode | MindmapRoot = this.tree,
    _options: {
      applyStyle?: boolean;
      layoutType?: LayoutType;
      calculateTreeBound?: boolean;
      stashed?: boolean;
    } = {
      applyStyle: true,
      calculateTreeBound: true,
      stashed: true,
    }
  ) {
    // should be implemented by the view
    // otherwise, it would be just an empty function
    if (this._layout) {
      this._layout(_tree, _options);
    }
  }

  moveTo(targetXYWH: SerializedXYWH | XYWH) {
    const { x, y } = this;
    const targetPos =
      typeof targetXYWH === 'string' ? deserializeXYWH(targetXYWH) : targetXYWH;
    const offsetX = targetPos[0] - x;
    const offsetY = targetPos[1] - y;

    this.surface.store.transact(() => {
      this.childElements.forEach(el => {
        const deserializedXYWH = deserializeXYWH(el.xywh);

        el.xywh =
          `[${deserializedXYWH[0] + offsetX},${deserializedXYWH[1] + offsetY},${deserializedXYWH[2]},${deserializedXYWH[3]}]` as SerializedXYWH;
      });
    });
  }

  override onCreated(): void {
    this.buildTree();
  }

  removeChild(element: GfxModel) {
    if (!this._nodeMap.has(element.id)) {
      return;
    }

    const surface = this.surface;
    const removedDescendants: string[] = [];
    const remove = (node: MindmapNode) => {
      node.children?.forEach(child => {
        remove(child);
      });

      this.children?.delete(node.id);
      removedDescendants.push(node.id);
    };

    surface.store.transact(() => {
      remove(this._nodeMap.get(element.id)!);
    });

    queueMicrotask(() => {
      removedDescendants.forEach(id => surface.deleteElement(id));
    });

    // This transaction may not end
    // force to build the elements
    this.buildTree();
    this.requestLayout();
  }

  protected requestBuildTree() {
    if (this._queueBuildTree) {
      return;
    }

    this._queueBuildTree = true;
    queueMicrotask(() => {
      this.buildTree();
      this._queueBuildTree = false;
    });
  }

  requestLayout() {
    if (!this._queuedLayout) {
      this._queuedLayout = true;

      queueMicrotask(() => {
        this.layout();
        this._queuedLayout = false;
      });
    }
  }

  override serialize() {
    const result = super.serialize();
    return result as SerializedMindmapElement;
  }

  setLayoutMethod(layoutMethod: MindmapElementModel['layout']) {
    this._layout = layoutMethod;
  }

  /**
   * Stash mind map node and its children's xywh property
   * @param node
   * @returns a function that write back the stashed xywh into yjs
   */
  stashTree(node: MindmapNode | string) {
    const mindNode = typeof node === 'string' ? this.getNode(node) : node;

    if (!mindNode || this._stashedNode.has(mindNode.id)) {
      return;
    }

    const stashed = new Set<GfxPrimitiveElementModel>();
    const traverse = (node: MindmapNode) => {
      node.element.stash('xywh');
      stashed.add(node.element);

      if (node.children.length) {
        node.children.forEach(child => traverse(child));
      }
    };

    traverse(mindNode);

    return () => {
      this._stashedNode.delete(mindNode.id);
      stashed.forEach(el => {
        el.pop('xywh');
      });
    };
  }

  toggleCollapse(node: MindmapNode, options: { layout?: boolean } = {}) {
    if (!this._nodeMap.has(node.id)) {
      return;
    }

    const { layout = false } = options;

    if (node && node.children.length > 0) {
      const collapsed = node.detail.collapsed ? false : true;
      const isExpand = !collapsed;

      const changeNodesVisibility = (node: MindmapNode) => {
        node.element.hidden = collapsed;

        if (isExpand && node.detail.collapsed) {
          return;
        }

        node.children.forEach(child => {
          changeNodesVisibility(child);
        });
      };

      node.children.forEach(changeNodesVisibility);
      this.surface.store.transact(() => {
        this.children.set(node.id, {
          ...node.detail,
          collapsed,
        });
      });
    }

    if (layout) {
      this.requestLayout();
    }
  }

  /** The root's first-level branches on one side of it. */
  getSideChildren(side: NodeSide): MindmapNode[] {
    // The tree is built lazily, and both of these are read from the layout and
    // the view, which can run before that happens.
    return (
      this._tree?.children.filter(child => this.getSide(child.id) === side) ?? []
    );
  }

  isSideCollapsed(side: NodeSide): boolean {
    return this._tree?.detail.collapsedSides?.includes(side) ?? false;
  }

  /**
   * Fold away just the branches on one side of the root, leaving the other side
   * alone. Only the balanced layouts have two sides; elsewhere `toggleCollapse`
   * on the root is the right thing.
   */
  toggleCollapseSide(side: NodeSide, options: { layout?: boolean } = {}) {
    const root = this._tree;
    const branches = this.getSideChildren(side);

    if (!branches.length) {
      return;
    }

    const collapsed = !this.isSideCollapsed(side);
    const isExpand = !collapsed;

    const changeVisibility = (node: MindmapNode) => {
      node.element.hidden = collapsed;

      // Re-expanding must not blow open a branch the user had folded shut on its
      // own; stop at it and leave its subtree hidden.
      if (isExpand && node.detail.collapsed) {
        return;
      }

      node.children.forEach(changeVisibility);
    };

    branches.forEach(changeVisibility);

    const sides = new Set(root.detail.collapsedSides ?? []);
    collapsed ? sides.add(side) : sides.delete(side);

    this.surface.store.transact(() => {
      this.children.set(root.id, {
        ...(this.children.get(root.id) ?? root.detail),
        collapsedSides: [...sides],
      });
    });

    if (options.layout ?? false) {
      this.requestLayout();
    }
  }

  /**
   * Unfold both sides of the root. Only the balanced layouts have a per-side
   * collapse button, so switching to a one-sided layout with a side folded away
   * would strand those branches hidden with nothing left to bring them back.
   */
  expandAllSides() {
    const root = this._tree;

    if (!root?.detail.collapsedSides?.length) {
      return;
    }

    const show = (node: MindmapNode) => {
      node.element.hidden = false;

      // A branch the user folded shut on its own stays shut.
      if (node.detail.collapsed) {
        return;
      }

      node.children.forEach(show);
    };

    root.children.forEach(show);

    this.surface.store.transact(() => {
      const detail = this.children.get(root.id);

      if (!detail) {
        return;
      }

      const { collapsedSides: _folded, ...rest } = detail;

      this.children.set(root.id, rest);
    });
  }

  traverse(
    callback: (node: MindmapNode, parent: MindmapNode | null) => void,
    root: MindmapNode = this._tree,
    options: { stopOnCollapse?: boolean } = {}
  ) {
    const { stopOnCollapse = false } = options;
    const traverse = (node: MindmapNode, parent: MindmapNode | null) => {
      callback(node, parent);

      if (stopOnCollapse && node.detail.collapsed) {
        return;
      }

      node?.children.forEach(child => {
        traverse(child, node);
      });
    };

    if (root) {
      traverse(root, null);
    }
  }

  @convert((initialValue, instance) => {
    if (!(initialValue instanceof Y.Map)) {
      nodeSchema.parse(initialValue);

      assertType<NodeType>(initialValue);

      const map: Y.Map<NodeDetail> = new Y.Map();
      const surface = instance.surface;
      const doc = surface.store;
      const recursive = (
        node: NodeType,
        parent: string | null = null,
        index: string = 'a0'
      ) => {
        const id = surface.addElement({
          type: 'shape',
          text: node.text,
          xywh: node.xywh ? node.xywh : `[0, 0, 100, 30]`,
        });

        map.set(id, {
          index,
          parent: parent ?? undefined,
        });

        let curIdx = 'a0';
        node.children?.forEach(childNode => {
          recursive(childNode, id, curIdx);
          curIdx = generateKeyBetween(curIdx, null);
        });
      };

      doc.transact(() => {
        recursive(initialValue);
      });

      instance.requestBuildTree();
      instance.requestLayout();
      return map;
    } else {
      instance.requestBuildTree();
      instance.requestLayout();
      return initialValue;
    }
  })
  // Use extracted function to avoid playwright test failure
  // since this model package is imported by playwright
  @observe(observeChildren)
  @field()
  accessor children: Y.Map<NodeDetail> = new Y.Map();

  @watch(watchLayoutType)
  @field()
  accessor layoutType: LayoutType = LayoutType.RIGHT;

  @watch(watchStyle)
  @field()
  accessor style: MindmapStyle = MindmapStyle.ONE;
}
